import {
  BlueskyRecommendationKey,
  ConcernTag,
  FBRecommendationKey,
  GoogleRecommendationKey,
  InstagramRecommendationKey,
  LinkedinRecommendationKey,
  RecommendationTaskType,
  RedditRecommendationKey,
  SnapchatRecommendationKey,
  StravaRecommendationKey,
  TiktokRecommendationKey,
  TwitterRecommendationKey,
  VenmoRecommendationKey,
  YoutubeRecommendationKey,
} from '../utils/enums'

export type RecommendationKeyEnum =
  | BlueskyRecommendationKey
  | FBRecommendationKey
  | GoogleRecommendationKey
  | InstagramRecommendationKey
  | VenmoRecommendationKey
  | TiktokRecommendationKey
  | TwitterRecommendationKey
  | SnapchatRecommendationKey
  | StravaRecommendationKey
  | LinkedinRecommendationKey
  | RedditRecommendationKey
  | YoutubeRecommendationKey

const recommendationKeyEnums = [
  BlueskyRecommendationKey,
  FBRecommendationKey,
  GoogleRecommendationKey,
  InstagramRecommendationKey,
  VenmoRecommendationKey,
  TiktokRecommendationKey,
  TwitterRecommendationKey,
  SnapchatRecommendationKey,
  StravaRecommendationKey,
  LinkedinRecommendationKey,
  RedditRecommendationKey,
  YoutubeRecommendationKey,
]

export const isValidRecommendationKey = (
  keyString: string,
): keyString is RecommendationKeyEnum =>
  recommendationKeyEnums.some((recommendationKeyEnum) =>
    Object.keys(recommendationKeyEnum).includes(keyString),
  )

export type RecommendationKeyEnumArray =
  | BlueskyRecommendationKey[]
  | FBRecommendationKey[]
  | GoogleRecommendationKey[]
  | InstagramRecommendationKey[]
  | VenmoRecommendationKey[]
  | SnapchatRecommendationKey[]
  | StravaRecommendationKey[]
  | TiktokRecommendationKey[]
  | TwitterRecommendationKey[]
  | LinkedinRecommendationKey[]
  | RedditRecommendationKey[]
  | YoutubeRecommendationKey[]

export type RecommendationRegistry =
  | Record<FBRecommendationKey, Recommendation>
  | Record<GoogleRecommendationKey, Recommendation>
  | Record<InstagramRecommendationKey, Recommendation>
  | Record<VenmoRecommendationKey, Recommendation>
  | Record<TiktokRecommendationKey, Recommendation>
  | Record<TwitterRecommendationKey, Recommendation>
  | Record<LinkedinRecommendationKey, Recommendation>
  | Record<SnapchatRecommendationKey, Recommendation>
  | Record<StravaRecommendationKey, Recommendation>
  | Record<RedditRecommendationKey, Recommendation>
  | Record<YoutubeRecommendationKey, Recommendation>
  | {}

export interface RecommendationMetadataSpec {
  createdOn: number
  estimatedTimeMs: number
  importanceScore: number
}

export interface RecommendationSpec {
  /**
   * Title displayed on the recommendation card
   */
  recommendationTitle: string

  /**
   * Short title used in smaller components such as tool cards
   */
  shortTitle?: string

  /**
   * The concern tags that are relevant to this recommendation
   */
  concernTags: ConcernTag[]

  /**
   * DEPRECATED (did not provide different explanation per concern tag)
   */
  explanationTextOld: string | null // DEPRECATED

  /**
   * The explanation text for the recommendation with different variations
   * for each concern tag to address specific concern
   */
  explanationTextByConcernTag:
    | {
        [key in ConcernTag]?: string
      }
    | null

  /**
   * Card body prompt (e.g. "Select settings to update")
   */
  bodyTitle: string

  /**
   * [Automated only] a list of settings to be scanned.
   * Each setting has a unique key and recommends a target value.
   */
  settings?: RecommendationSetting[]

  /**
   * [Manual only] a list of steps that guide the user to complete the recommendation.
   */
  steps?: RecommendationStep[]

  /**
   * Additional text that can be displayed in the card footer to give the user
   * more context about the scan or update (e.g. "Undoing this requires manual
   * authentication")
   */
  customActionDescription?: string

  /**
   * The type of recommendation card body to display (automated, manual, fb photo untagging, task)
   */
  recommendationCardType: string

  /**
   * Whether the recommendation has an AutomatedBody (with settings)
   */
  isAutomated: boolean

  /**
   * Whether the recommendation has automated gather stages
   */
  hasAutomatedGather?: boolean

  /**
   * Can the entire recommendation be undone
   */
  canUndo: boolean

  /**
   * The account types that the recommendation is relevant to. Currently only
   * used for Instgram as the account type (private, public, business, etc.)
   * impacts the settings available.
   */
  accountTypes?: string | string[]

  /**
   * The frequency at which the recommendation should be re-included in a full
   * scan if it was previously completed
   */
  rescanFrequencyDays?: number

  /**
   * Deeplink url to this recommendation that can be shared externally
   */
  deeplinkShareUrl?: string

  /**
   * Is this a bonus recommendation? Bonus recommendations are displayed in
   * a collapsed section in the sidebar
   */
  bonus?: boolean

  /**
   * EXPERIMENT TASK PROPERTIES
   *
   * The below properties were introduced during experiment implementation
   * Summer 2024. They could use further optimization and organization.
   */
  isTask?: boolean

  /**
   * Akin to bodyTitle. The title displayed in the card body.
   */
  taskTitle?: string

  /**
   * Type of task: Content or Experience
   */
  taskType?: RecommendationTaskType

  /**
   * Task content that is displayed as a bulleted list in the card body
   */
  taskContext?: string[]

  /**
   * Main task CTA button text
   */
  taskButtonText?: string

  /**
   * Similar to customActionDescription. Additional text that can be displayed
   * in the card footer to give the user more context about the task (e.g.
   * "This bulk action can't be undone, but posts can be un-archived one by one.")
   */
  taskWarningText?: string

  /**
   * Confirmation button text. All tasks ask for additional confirmation since
   * the actions are more destructive.
   */
  confirmTaskButtonText?: string

  /**
   * Separate button CTA for subsequent task runs, e.g. "Delete More Posts"
   */
  continuingTaskButtonText?: string

  /**
   * Text that displays in the card footer after update
   */
  taskUpdatedText?: string
}

// Value for RecommendationSpec field rescanFrequencyDays that indicates never
export const NEVER_RESCAN_FREQUENCY_DAYS = -1

export interface Recommendation extends RecommendationSpec {
  key: RecommendationKeyEnum

  /**
   * Does this recommendation operate on photos? Card will show different icons
   * and a different CTA
   */
  isPhoto?: boolean

  /**
   * A property that was introduced to indicate that the automation run time of
   * the recommendation may vary depending on some aspect of the user's account
   * such as the amount of photos or albums they have.
   *
   * Used in automation overlay to display different progress messaging to the user.
   */
  isUserActivityDependent?: boolean
}

export interface RecommendationSetting {
  description: string
  key: string
}

export interface RecommendationStep {
  description: string
  id: number
}
